// frontend/src/pages/Login.js
import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import '../styles/Auth.css';
import './Login.css';
import { AuthContext } from '../context/AuthContext';
import QBackground from '../components/QBackground';
import LanguageSelector from '../components/LanguageSelector';
import { useI18n } from '../i18n';
import AntahkaranaLogo from '../components/AntahkaranaLogo';

// Aceptamos la prop onLoginSuccess que viene de App.js
function Login({ onLoginSuccess }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useContext(AuthContext);
  const { t } = useI18n();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('credentials'); // 'credentials', 'otp', 'forgot_email', 'forgot_otp', 'forgot_reset'
  const [emailForOtp, setEmailForOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  // Easter Egg: Secret Q Counter
  const [qCount, setQCount] = useState(0);
  const qCountRef = useRef(0);
  const [showCounter, setShowCounter] = useState(false);
  const [isPenalty, setIsPenalty] = useState(false);
  const penaltyTimerRef = useRef(null);
  const timerRef = useRef(null);
  const skipWarningRef = useRef(false);
  const processedTokenRef = useRef(null);

  useEffect(() => {
    qCountRef.current = qCount;
  }, [qCount]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (qCount > 0 && !skipWarningRef.current) {
        const message = t('auth.leaveWarning');
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [qCount, t]);

  const saveQConnections = useCallback(async (tokenToUse, manualCount = null) => {
    const countToSave = manualCount !== null ? manualCount : qCountRef.current;
    if (countToSave > 0) {
      try {
        await api.post('/users/q-connections',
          { count: countToSave },
          { headers: { Authorization: `Bearer ${tokenToUse}` } }
        );
      } catch (err) {
        console.warn("No se pudieron guardar las conexiones Q", err);
      }
    }
  }, []);

  const handleNodesConnected = useCallback((count) => {
    setQCount(prev => prev + count);
    setShowCounter(true);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setShowCounter(false);
    }, 3000); // Se oculta después de 3 segundos
  }, []);

  const handleNodeMissed = useCallback(() => {
    setQCount(prev => {
      if (prev > 0) {
        // Activar el destello rojo
        setIsPenalty(true);
        if (penaltyTimerRef.current) clearTimeout(penaltyTimerRef.current);
        penaltyTimerRef.current = setTimeout(() => setIsPenalty(false), 500); // 500ms de color rojo

        return prev - 1;
      }
      return prev;
    });
  }, []);

  // Efecto para capturar el token que devuelve Google
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let token = params.get('token');
    const errorMsg = params.get('error');

    // Intentar leer del hash si no está en query params (Seguridad mejorada)
    if (!token && location.hash) {
      const hashParams = new URLSearchParams(location.hash.substring(1)); // remover '#'
      token = hashParams.get('token');
    }

    if (token && processedTokenRef.current !== token) {
      processedTokenRef.current = token;
      const processGoogleLogin = async () => {
        localStorage.setItem('token', token);

        if (qCount > 0) {
          await saveQConnections(token);
        }

        login(token);

        if (onLoginSuccess) {
          onLoginSuccess(token);
          return;
        }

        navigate('/');
      };
      processGoogleLogin();
    }

    if (errorMsg) {
      setError(t('auth.googleError'));
    }
  }, [location, login, navigate, onLoginSuccess, qCount, saveQConnections, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await api.post('/auth/login', { username, password });

      if (response.data.requireOtp) {
        setStep('otp');
        setEmailForOtp(response.data.email);
        return;
      }

      const { token } = response.data;
      handleLoginSuccess(token);
    } catch (err) {
      console.error('Login error:', err);
      const msg = err.response?.data?.error || t('auth.loginError');
      setError(msg);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await api.post('/auth/verify-otp', { email: emailForOtp, otp });
      const { token } = response.data;
      handleLoginSuccess(token);
    } catch (err) {
      console.error('OTP error:', err);
      const msg = err.response?.data?.error || t('auth.invalidOtp');
      setError(msg);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.post('/auth/forgot-password', { email: emailForOtp });
      setStep('forgot_otp');
    } catch (err) {
      setError(err.response?.data?.error || t('auth.sendOtpError'));
    }
  };

  const handleForgotOtpSubmit = (e) => {
    e.preventDefault();
    setStep('forgot_reset');
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    try {
      const response = await api.post('/auth/reset-password', {
        email: emailForOtp,
        otp,
        newPassword
      });
      const { token } = response.data;
      handleLoginSuccess(token);
    } catch (err) {
      setError(err.response?.data?.error || t('auth.resetError'));
    }
  };

  const handleLoginSuccess = async (token) => {
    localStorage.setItem('token', token);

    if (qCount > 0) {
      await saveQConnections(token);
    }

    login(token);

    if (onLoginSuccess) {
      onLoginSuccess(token);
      return;
    }

    navigate('/');
  };

  const handleGoogleLogin = () => {
    // Si hay conexiones, las guardamos temporalmente antes del redirect
    if (qCount > 0) {
      sessionStorage.setItem('pendingQConnections', qCount.toString());
    }
    skipWarningRef.current = true;

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';
    window.location.href = `${API_URL}/api/auth/google`;
  };

  return (
    <div className="auth-container">
      <QBackground onNodesConnected={handleNodesConnected} onNodeMissed={handleNodeMissed} />
      <LanguageSelector compact className="auth-language-selector" />

      <div className="auth-top-right-stack">
        <div className="auth-top-nav">
          <button
            type="button"
            className="auth-cyber-btn purple-variant"
            onClick={() => navigate('/register')}
          >
            {t('auth.register')}
          </button>
          <span className="auth-nav-caption">{t('auth.noAccountYet')}</span>
        </div>

        {/* Secret Q Counter Easter Egg (Moved to bottom of stack) */}
        <div
          className={`secret-q-counter ${showCounter ? 'visible' : ''} ${isPenalty ? 'penalty' : ''}`}
          onMouseEnter={() => setShowCounter(true)}
          onMouseLeave={() => {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setShowCounter(false), 2000);
          }}
        >
          {String(qCount).padStart(4, '0')}
        </div>
      </div>

      <AntahkaranaLogo className="auth-logo-header" size={220} style={{ display: 'block', margin: '0 auto 20px auto' }} />

      {step === 'credentials' && (
        <form className="auth-form" onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder={t('auth.username')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder={t('auth.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            type="button"
            className="forgot-password-btn"
            onClick={() => setStep('forgot_email')}
          >
            {t('auth.forgotPassword')}
          </button>
          <div className="auth-action-group">
            <button type="submit" className="auth-cyber-btn">{t('auth.next')}</button>
          </div>
        </form>
      )}

      {step === 'otp' && (
        <form className="auth-form" onSubmit={handleOtpSubmit}>
          <p style={{ color: 'white', marginBottom: '10px', textAlign: 'center' }}>
            {t('auth.otpInstruction', { email: emailForOtp })}
          </p>
          <input
            type="text"
            placeholder={t('auth.otpPlaceholder')}
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            maxLength="4"
            required
            autoFocus
          />
          <button type="submit">{t('auth.verifyEnter')}</button>
          <button type="button" onClick={() => setStep('credentials')} className="btn-back">
            {t('common.back')}
          </button>
        </form>
      )}

      {step === 'forgot_email' && (
        <form className="auth-form" onSubmit={handleForgotPasswordSubmit}>
          <p style={{ color: 'white', marginBottom: '10px', textAlign: 'center' }}>
            {t('auth.forgotEmailInstruction')}
          </p>
          <input
            type="email"
            placeholder={t('auth.yourEmail')}
            value={emailForOtp}
            onChange={(e) => setEmailForOtp(e.target.value)}
            required
            autoFocus
          />
          <button type="submit">{t('auth.sendCode')}</button>
          <button type="button" onClick={() => setStep('credentials')} className="btn-back">
            {t('common.back')}
          </button>
        </form>
      )}

      {step === 'forgot_otp' && (
        <form className="auth-form" onSubmit={handleForgotOtpSubmit}>
          <p style={{ color: 'white', marginBottom: '10px', textAlign: 'center' }}>
            {t('auth.otpInstruction', { email: emailForOtp })}
          </p>
          <input
            type="text"
            placeholder={t('auth.otpPlaceholder')}
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            maxLength="4"
            required
            autoFocus
          />
          <button type="submit">{t('auth.verifyCode')}</button>
          <button type="button" onClick={() => setStep('forgot_email')} className="btn-back">
            {t('common.back')}
          </button>
        </form>
      )}

      {step === 'forgot_reset' && (
        <form className="auth-form" onSubmit={handleResetPasswordSubmit}>
          <p style={{ color: 'white', marginBottom: '10px', textAlign: 'center' }}>
            {t('auth.forgotResetInstruction')}
          </p>
          <input
            type="password"
            placeholder={t('auth.newPassword')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            autoFocus
          />
          <input
            type="password"
            placeholder={t('auth.confirmPassword')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          <button type="submit">{t('auth.changePasswordEnter')}</button>
          <button type="button" onClick={() => setStep('forgot_otp')} className="btn-back">
            {t('common.back')}
          </button>
        </form>
      )}

      <div className="google-login-container" style={{ marginTop: '20px', width: '100%', display: 'flex', justifyContent: 'center' }}>
        <button onClick={handleGoogleLogin} className="auth-google-btn">
          <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google" style={{ width: '20px', height: '20px' }} />
          {t('auth.googleLogin')}
        </button>
      </div>

      {error && <p className="auth-error">{error}</p>}
    </div>
  );
}

export default Login;
